import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, CheckCheck, X } from "lucide-react";

const POLL_MS = 60_000;

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(false);
  const popRef = useRef(null);
  const nav = useNavigate();

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread_count || 0);
    } catch {}
  }, []);

  const loadList = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {} finally { setBusy(false); }
  }, []);

  // Initial + interval poll for unread count
  useEffect(() => {
    loadCount();
    const id = setInterval(loadCount, POLL_MS);
    return () => clearInterval(id);
  }, [loadCount]);

  // Load full list when opening
  useEffect(() => {
    if (open) loadList();
  }, [open, loadList]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch {}
  };

  const onClickItem = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.notif_id}/read`);
        setItems((arr) => arr.map((x) => x.notif_id === n.notif_id ? { ...x, read: true } : x));
        setUnread((u) => Math.max(0, u - 1));
      } catch {}
    }
    if (n.link) nav(n.link);
    setOpen(false);
  };

  return (
    <div className="relative" ref={popRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="nav-notif-bell"
        title="Notifications"
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-full border-2 border-black bg-linen text-black hover:bg-mustard transition"
      >
        <Bell className="w-4 h-4" strokeWidth={2} />
        {unread > 0 && (
          <span
            data-testid="nav-notif-count"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full border-2 border-black bg-wine text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-popover"
          className="absolute right-0 mt-3 w-[340px] bg-linen border-2 border-black rounded-[1.25rem] overflow-hidden shadow-xl"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b-2 border-black/15">
            <p className="font-serif text-lg text-ink">Notifications</p>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  data-testid="notif-mark-all"
                  title="Mark all read"
                  className="inline-flex items-center gap-1 text-xs font-medium text-wine hover:underline"
                >
                  <CheckCheck className="w-3.5 h-3.5" strokeWidth={2} /> Mark all
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="w-6 h-6 rounded-full hover:bg-black/5 flex items-center justify-center"
              >
                <X className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </div>
          </div>

          <div className="max-h-[380px] overflow-y-auto" data-testid="notif-list">
            {busy && items.length === 0 ? (
              <p className="px-4 py-8 text-sm text-muted text-center">Loading…</p>
            ) : items.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="w-6 h-6 mx-auto text-black/30 mb-2" strokeWidth={1.5} />
                <p className="text-sm text-muted">You're all caught up.</p>
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.notif_id}
                  onClick={() => onClickItem(n)}
                  data-testid={`notif-item-${n.notif_id}`}
                  className={`w-full text-left px-4 py-3 border-b-2 border-black/10 last:border-b-0 transition flex gap-3 items-start ${
                    n.read ? "bg-linen hover:bg-black/5" : "bg-mustard/30 hover:bg-mustard/50"
                  }`}
                >
                  {!n.read && <span className="w-2 h-2 mt-1.5 rounded-full bg-wine shrink-0" />}
                  <div className={`flex-1 min-w-0 ${n.read ? "" : "ml-0"}`}>
                    <p className={`text-sm leading-snug ${n.read ? "text-ink/80" : "text-ink font-medium"}`}>
                      {n.title}
                    </p>
                    {n.body && <p className="text-xs text-black/60 mt-0.5 truncate">{n.body}</p>}
                    <p className="text-[10px] text-black/40 mt-1 uppercase tracking-wider">
                      {timeAgo(n.created_at)}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const s = Math.max(1, Math.round((Date.now() - t) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}
